import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '@/api/client';
import { AUTH_ENDPOINTS, USER_ENDPOINTS } from '@/api/endpoints';
import { useAuthStore } from '@/auth/useAuthStore';
import type {
  AuthUser,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  Usuario,
  UpdateUsuarioForm,
} from '@/types';
import { QUERY_KEYS } from '@/types';

// Utility to decode JWT token (simple decode, not verification)
const decodeToken = (token: string): AuthUser | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      _id: payload.sub,
      email: payload.email,
      nombre: payload.nombre || payload.email,
      role: payload.role,
    };
  } catch {
    return null;
  }
};

// Login mutation
export const useLogin = () => {
  const { setAuth, setLoading } = useAuthStore();

  return useMutation({
    mutationFn: async (credentials: LoginRequest): Promise<LoginResponse> => {
      const response = await apiClient.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, credentials);
      return response.data;
    },
    onSuccess: (data) => {
      const user = decodeToken(data.accessToken);
      if (user) {
        // Store refresh token in localStorage (temporary solution)
        localStorage.setItem('refreshToken', data.refreshToken);
        setAuth(user, data.accessToken);
      }
    },
    onError: () => {
      setLoading(false);
    },
  });
};

// Silent refresh function (for bootstrapping session)
export const useRefreshToken = () => {
  const { setAuth, clearAuth, setLoading } = useAuthStore();

  return useMutation({
    mutationFn: async (): Promise<RefreshTokenResponse> => {
      // Get refresh token from localStorage (temporary solution)
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No hay token de refresco disponible');
      }

      const response = await apiClient.post<RefreshTokenResponse>(
        AUTH_ENDPOINTS.REFRESH,
        { refreshToken },
        {
          withCredentials: true,
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      const user = decodeToken(data.accessToken);
      if (user) {
        setAuth(user, data.accessToken);
      } else {
        clearAuth();
      }
    },
    onError: (error: any) => {
      // Only log if it's not a network error (backend not running)
      if (error?.statusCode !== undefined) {
        console.log('Fallo al refrescar la sesión:', error.message);
      }
      clearAuth();
    },
    onSettled: () => {
      setLoading(false);
    },
    // Prevent automatic retries for auth refresh
    retry: false,
  });
};

// Change password mutation
export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ChangePasswordRequest) => {
      console.log('🔑 Starting password change request');
      
      try {
        const response = await apiClient.patch(AUTH_ENDPOINTS.CHANGE_PASSWORD, data);
        console.log('✅ Cambio de contraseña exitoso');
        return response.data;
      } catch (error: any) {
        console.log('❌ Error al cambiar la contraseña capturado:', {
          status: error?.response?.status,
          statusCode: error?.statusCode,
          message: error?.response?.data?.message || error?.message,
          isAxiosError: error?.isAxiosError,
          errorType: error?.constructor?.name,
        });

        // Handle password change specific 401 errors
        if (error?.response?.status === 401) {
          console.log('🔑 Manejo de error 401 para cambio de contraseña');
          // This is likely an "incorrect old password" error
          // Create a custom error that won't trigger the logout flow
          const passwordError = {
            statusCode: 401,
            message: error?.response?.data?.message || 'La contraseña actual es incorrecta',
            error: 'InvalidPassword',
            isPasswordError: true,
          };
          console.log('🔑 Lanzando error personalizado de contraseña:', passwordError);
          throw passwordError;
        }
        // Re-throw other errors normally
        console.log('🔑 Re-lanzando error distinto de 401');
        throw error;
      }
    },
    onSuccess: () => {
      console.log('🔑 onSuccess de cambio de contraseña ejecutado');
      // Invalidate any user-related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error) => {
      console.log('🔑 onError de cambio de contraseña ejecutado:', error);
    },
  });
};

// Get current user profile (role-based endpoint selection)
export const useCurrentUser = () => {
  const { user, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: [QUERY_KEYS.USUARIO, 'me'],
    queryFn: async (): Promise<Usuario> => {
      if (!user?._id) {
        throw new Error('Usuario no autenticado');
      }

      let response;
      
      if (user.role === 'ADMIN') {
        // Admins use the full user management endpoint
        console.log('🔑 Usuario ADMIN - usando endpoint /usuarios/:id');
        response = await apiClient.get<Usuario>(USER_ENDPOINTS.GET(user._id));
      } else {
        // Other users use the profile endpoint
        console.log('🔑 Usuario no ADMIN - usando endpoint /auth/profile');
        response = await apiClient.get<Usuario>(AUTH_ENDPOINTS.PROFILE);
      }
      
      return response.data;
    },
    enabled: isAuthenticated && !!user?._id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Update current user profile (role-based endpoint selection)
export const useUpdateCurrentUser = () => {
  const { user, setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<UpdateUsuarioForm, 'activo'>): Promise<Usuario | { message: string }> => {
      if (!user?._id) {
        throw new Error('Usuario no autenticado');
      }

      let response;
      
      if (user.role === 'ADMIN') {
        // Admins use the full user management endpoint (PUT /usuarios/:id)
        console.log('🔑 Usuario ADMIN - usando endpoint PUT /usuarios/:id');
        response = await apiClient.put<Usuario>(USER_ENDPOINTS.UPDATE(user._id), data);
        return response.data; // Returns full user object
      } else {
        // Other users use the profile endpoint (PATCH /auth/profile)
        console.log('🔑 Usuario no ADMIN - usando endpoint PATCH /auth/profile');
        // Only send 'nombre' for non-admin users (they can't change role/activo)
        const profileData = { nombre: data.nombre };
        response = await apiClient.patch<{ message: string }>(AUTH_ENDPOINTS.UPDATE_PROFILE, profileData);
        
        // For non-admin users, we need to fetch the updated profile since PATCH only returns success message
        const profileResponse = await apiClient.get<Usuario>(AUTH_ENDPOINTS.PROFILE);
        return profileResponse.data;
      }
    },
    onSuccess: (result) => {
      // Handle both response types (Usuario object or success message)
      const updatedUser = 'message' in result ? null : result as Usuario;
      
      if (updatedUser && user) {
        // Update auth store with new user data
        const updatedAuthUser: AuthUser = {
          ...user,
          nombre: updatedUser.nombre,
          role: updatedUser.role,
        };
        setAuth(updatedAuthUser, useAuthStore.getState().accessToken!);
        
        // Update cache with full user object
        queryClient.setQueryData([QUERY_KEYS.USUARIO, 'me'], updatedUser);
      }
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USUARIO] });
    },
  });
};

// Logout function (not a mutation since it's local only)
export const useLogout = () => {
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return () => {
    // Clear refresh token from localStorage
    localStorage.removeItem('refreshToken');
    clearAuth();
    queryClient.clear(); // Clear all cached data
    
    // Optional: Call logout endpoint if backend implements it
    // This would handle server-side session cleanup
    apiClient.post('/auth/logout', {}, { withCredentials: true }).catch(() => {
      // Ignore errors - user is logging out anyway
    });
  };
};